import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import {
  TrendUp, Funnel, Package, Trophy, ClockCounterClockwise, CurrencyInr,
  ArrowRight, Phone, WhatsappLogo, Calendar, Target, Users, CheckCircle, XCircle
} from "@phosphor-icons/react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";

const STATUS_META = {
  new: { label: "New", color: "#3b82f6" },
  contacted: { label: "Contacted", color: "#6366f1" },
  follow_up: { label: "Follow Up", color: "#f59e0b" },
  won: { label: "Won", color: "#10b981" },
  lost: { label: "Lost", color: "#f43f5e" },
};
const SOURCE_COLORS = ["#002FA7", "#f59e0b", "#10b981", "#6366f1", "#f43f5e", "#8b5cf6", "#14b8a6"];

const fmtInr = (v) => `₹${(Number(v) || 0).toLocaleString("en-IN")}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} · ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
};
const hoursFromNow = (iso) => {
  if (!iso) return null;
  return Math.round((new Date(iso).getTime() - Date.now()) / 3600000);
};

export default function LeadsDashboard({ user, logout }) {
  const navigate = useNavigate();
  const role = user?.role || "";
  const canAccess = ["admin", "manager", "bdm", "bde", "marketing_head"].includes(role);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canAccess) return;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/product-leads/stats`, {
          params: { requester_role: role, requester_name: user?.name || "" },
        });
        setStats(data);
      } catch (err) {
        toast.error("Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(t);
  }, [canAccess, role, user?.name]);

  const funnel = useMemo(() => {
    if (!stats) return [];
    const bs = stats.by_status || {};
    return [
      { stage: "New", count: bs.new || 0, color: STATUS_META.new.color },
      { stage: "Contacted", count: bs.contacted || 0, color: STATUS_META.contacted.color },
      { stage: "Follow Up", count: bs.follow_up || 0, color: STATUS_META.follow_up.color },
      { stage: "Won", count: bs.won || 0, color: STATUS_META.won.color },
    ];
  }, [stats]);

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar user={user} logout={logout} />
        <div className="ml-64 p-12">
          <div className="bg-white border border-amber-200 p-8 text-center max-w-md mx-auto">
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Access Restricted</h2>
            <p className="text-sm text-slate-600">Leads Dashboard is available to Admin, Manager, BDM, BDE and Marketing Head.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="ml-64">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-20">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Leads Dashboard</h1>
              <p className="text-xs text-slate-500 mt-0.5">Live pipeline health · refreshes every 60s · {role === "bde" ? "Your leads only" : "Team view"}</p>
            </div>
            <button onClick={() => navigate("/admin/leads")} data-testid="open-leads-btn"
              className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 text-xs font-bold flex items-center gap-1.5">
              Open Leads Pipeline <ArrowRight size={12} weight="bold" />
            </button>
          </div>
        </div>

        {loading || !stats ? (
          <div className="p-12 text-center text-slate-500">Loading…</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
              <KpiTile testid="tile-total" label="Total Leads" value={stats.total} icon={<Users size={18} weight="fill" />} tone="slate" />
              <KpiTile testid="tile-mtd" label="Leads (MTD)" value={stats.leads_mtd} icon={<Calendar size={18} weight="fill" />} tone="blue" />
              <KpiTile testid="tile-pending" label="Pending Follow-ups" value={stats.pending_follow_ups} icon={<ClockCounterClockwise size={18} weight="fill" />} tone="amber" />
              <KpiTile testid="tile-overdue" label="Overdue" value={stats.overdue_follow_ups} icon={<XCircle size={18} weight="fill" />} tone="rose" />
              <KpiTile testid="tile-conversion" label="Conversion Rate" value={`${stats.conversion_rate}%`} icon={<Target size={18} weight="fill" />} tone="emerald" />
              <KpiTile testid="tile-revenue" label="Won Revenue (MTD)" value={fmtInr(stats.won_revenue_mtd)} sub={`${stats.won_count_mtd} deals`} icon={<CurrencyInr size={18} weight="fill" />} tone="emerald" />
            </div>

            {/* Trend chart */}
            <Card title="Lead Trend (Last 14 days)" testid="card-trend">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.trend_14d || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="leads" stroke="#002FA7" strokeWidth={2} name="New Leads" dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="won" stroke="#10b981" strokeWidth={2} name="Won" dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* 2-column: Funnel + Source */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card title="Conversion Funnel" testid="card-funnel" className="lg:col-span-2">
                <FunnelChart data={funnel} total={stats.total} />
              </Card>

              <Card title="Lead Sources" testid="card-sources">
                {(stats.source_breakdown || []).length === 0 ? (
                  <Empty text="No leads yet." />
                ) : (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={stats.source_breakdown} dataKey="count" nameKey="source"
                          cx="50%" cy="50%" outerRadius={70} label={({ source, percent }) => `${source}: ${(percent * 100).toFixed(0)}%`}
                          labelLine={false} fontSize={10}>
                          {stats.source_breakdown.map((entry, i) => <Cell key={`c-${entry.source}`} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>
            </div>

            {/* Follow-up widget + Top products */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card title={`Upcoming Follow-ups (${(stats.upcoming_follow_ups || []).length})`} testid="card-follow-ups"
                action={<button onClick={() => navigate("/admin/leads")} className="text-[11px] text-[#002FA7] hover:underline font-bold">Open Pipeline →</button>}>
                <FollowUpList items={stats.upcoming_follow_ups || []} onOpen={(l) => navigate(`/admin/leads?lead=${l.lead_id}`)} />
              </Card>

              <Card title="Top Products by Enquiry" testid="card-top-products">
                <TopProducts rows={stats.top_products || []} navigate={navigate} />
              </Card>
            </div>

            {/* Top performers leaderboard */}
            {role !== "bde" && (
              <Card title="Top Performers (by Conversion)" testid="card-performers">
                <TopPerformers rows={stats.top_performers || []} />
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiTile({ label, value, sub, icon, tone = "slate", testid }) {
  const map = {
    slate: "bg-slate-50 border-slate-200 text-slate-600",
    blue: "bg-blue-50 border-blue-200 text-blue-600",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    rose: "bg-rose-50 border-rose-200 text-rose-600",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  };
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 p-3 flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">{label}</div>
        <div className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
        {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
      </div>
      <div className={`p-2 border ${map[tone]}`}>{icon}</div>
    </div>
  );
}

function Card({ title, children, testid, action, className = "" }) {
  return (
    <div data-testid={testid} className={`bg-white border border-slate-200 ${className}`}>
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h3>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-center py-10 text-xs text-slate-400 italic">{text}</div>;
}

function FunnelChart({ data, total }) {
  if (total === 0) return <Empty text="No leads in the pipeline yet." />;
  return (
    <div className="space-y-2">
      {data.map((d, i) => {
        const pct = total > 0 ? (d.count / total) * 100 : 0;
        const convFromPrev = i > 0 && data[i - 1].count > 0 ? (d.count / data[i - 1].count) * 100 : null;
        return (
          <div key={d.stage} data-testid={`funnel-${d.stage.toLowerCase().replace(" ", "-")}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-bold text-slate-700 uppercase tracking-wider">{d.stage}</span>
              <span className="text-slate-500">
                <b className="text-slate-900">{d.count}</b> leads{convFromPrev !== null && <span className="ml-2 text-[10px]">({convFromPrev.toFixed(0)}% vs prev)</span>}
              </span>
            </div>
            <div className="h-7 bg-slate-100 rounded-sm overflow-hidden relative">
              <div className="h-full transition-all duration-700 flex items-center justify-end pr-2"
                style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: d.color }}>
                <span className="text-white text-[10px] font-bold">{pct.toFixed(0)}%</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FollowUpList({ items, onOpen }) {
  if (items.length === 0) return <Empty text="No follow-ups scheduled in the next 7 days." />;
  return (
    <div className="space-y-2 max-h-80 overflow-y-auto">
      {items.map(l => {
        const hrs = hoursFromNow(l.follow_up_at);
        const overdue = hrs !== null && hrs < 0;
        const soon = hrs !== null && hrs >= 0 && hrs <= 24;
        return (
          <div key={l.lead_id} data-testid={`fu-${l.lead_id}`}
            className={`border-l-4 ${overdue ? "border-rose-500 bg-rose-50" : soon ? "border-amber-500 bg-amber-50" : "border-slate-300 bg-slate-50"} px-3 py-2 flex items-center justify-between gap-2 cursor-pointer hover:shadow`}
            onClick={() => onOpen(l)}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-slate-900 truncate">{l.name}</span>
                {overdue && <span className="text-[9px] bg-rose-500 text-white px-1 py-0.5 font-bold">OVERDUE</span>}
                {soon && !overdue && <span className="text-[9px] bg-amber-500 text-white px-1 py-0.5 font-bold">TODAY</span>}
              </div>
              <div className="text-[11px] text-slate-600 truncate">{l.product_name} · ×{l.quantity} · {fmtDate(l.follow_up_at)}</div>
              <div className="text-[10px] text-slate-500">👤 {l.assigned_to || "Unassigned"}</div>
            </div>
            <div className="flex gap-1 shrink-0">
              {l.phone && (
                <a href={`tel:${l.phone}`} onClick={(e) => e.stopPropagation()} className="p-1.5 bg-white border border-slate-200 hover:border-[#002FA7]" title="Call">
                  <Phone size={12} className="text-[#002FA7]" />
                </a>
              )}
              {l.phone && (
                <a href={`https://wa.me/${(l.phone || "").replace(/[^\d]/g, "")}?text=${encodeURIComponent(`Hi ${l.name}, about your enquiry for ${l.product_name} — `)}`}
                  target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                  className="p-1.5 bg-white border border-slate-200 hover:border-green-500" title="WhatsApp">
                  <WhatsappLogo size={12} className="text-green-600" />
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TopProducts({ rows, navigate }) {
  if (rows.length === 0) return <Empty text="No product enquiries yet." />;
  const max = Math.max(...rows.map(r => r.count), 1);
  return (
    <div className="space-y-2">
      {rows.map(r => (
        <div key={r.product_id || r.product_name} data-testid={`tp-${r.product_id || "x"}`}
          className="flex items-center gap-3 hover:bg-slate-50 p-1.5 cursor-pointer"
          onClick={() => r.product_id && navigate(`/products/${r.product_id}`)}>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-slate-900 truncate">{r.product_name}</div>
            <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
              <div className="h-full bg-[#002FA7]" style={{ width: `${(r.count / max) * 100}%` }} />
            </div>
          </div>
          <div className="text-right shrink-0 w-20">
            <div className="text-sm font-bold text-slate-900">{r.count}</div>
            {r.won_count > 0 && <div className="text-[10px] text-emerald-600">{r.won_count} won</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function TopPerformers({ rows }) {
  if (rows.length === 0) return <Empty text="No assigned leads yet." />;
  const medals = ["🥇", "🥈", "🥉"];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left">
            <th className="py-2 pr-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Rank</th>
            <th className="py-2 pr-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Name</th>
            <th className="py-2 pr-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Leads</th>
            <th className="py-2 pr-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Won</th>
            <th className="py-2 pr-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Conv %</th>
            <th className="py-2 pr-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold text-right">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.user_id || r.name} data-testid={`perf-${r.user_id || i}`} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="py-2 pr-2">{medals[i] || i + 1}</td>
              <td className="py-2 pr-2 font-bold text-slate-900">{r.name}</td>
              <td className="py-2 pr-2">{r.total}</td>
              <td className="py-2 pr-2 text-emerald-700 font-bold">{r.won}</td>
              <td className="py-2 pr-2">
                <div className="flex items-center gap-2">
                  <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${Math.min(r.conversion_rate, 100)}%` }} />
                  </div>
                  <span className="text-xs">{r.conversion_rate}%</span>
                </div>
              </td>
              <td className="py-2 pr-2 text-right font-bold text-[#002FA7]">{fmtInr(r.won_revenue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
